import { BTIndexedDB } from "types/IndexedDB/BTIndexedDB";
import { useIndexedDB } from "types/IndexedDB/useIndexedDB";

import { type ScheduleDraftScheduleItem } from "handlers";

import { type IDraftHistoryState } from "entity/schedule/ScheduleDrafts/ScheduleDrafts.types";

const SCHEMA_VERSION = 1;
const STORE_NAME = "DraftHistory";
const KEY_PATH = ["builderId", "jobId"];

export interface IDraftHistoryStateStorage extends IDraftHistoryState {
    schemaVersion: number;
    timestamp: number;
    builderId: number;
    jobId?: number;
}

export async function getAllDraftsForBuilder(
    builderId: number,
): Promise<IDraftHistoryStateStorage[]> {
    const db = new BTIndexedDB<IDraftHistoryStateStorage, [number, number]>(STORE_NAME, KEY_PATH);
    const allDrafts = await db.getAll();
    return allDrafts.filter((draft) => draft.builderId === builderId);
}
export async function getDraftsForJob(
    jobId: number,
): Promise<IDraftHistoryStateStorage | undefined> {
    const db = new BTIndexedDB<IDraftHistoryStateStorage, [number, number]>(STORE_NAME, KEY_PATH);
    const jobDrafts = await db.getAll();
    return jobDrafts.find((draft) => draft.jobId === jobId);
}

export async function getDraftSchedule(
    builderId: number,
    jobId: number,
): Promise<ScheduleDraftScheduleItem[] | null> {
    const db = new BTIndexedDB<IDraftHistoryStateStorage, [number, number]>(STORE_NAME, KEY_PATH);
    const item = await db.get([builderId, jobId]);
    if (!item) {
        return null;
    }
    return item.current.currentState.items;
}

export function useDraftStorage(builderId: number, jobId?: number) {
    const { getValue, setValue, removeValue } = useIndexedDB<
        IDraftHistoryStateStorage,
        [number, number]
    >(STORE_NAME, KEY_PATH);

    const setDraft = async (state: IDraftHistoryState): Promise<void> => {
        if (!jobId) {
            throw new Error("jobId is required to set a draft");
        }
        const draftState: IDraftHistoryStateStorage = {
            ...state,
            schemaVersion: SCHEMA_VERSION,
            timestamp: Date.now(),
            builderId,
            jobId,
        };
        await setValue([builderId, jobId], draftState);
    };

    const getDraft = async (): Promise<IDraftHistoryStateStorage | null> => {
        if (!jobId) {
            throw new Error("jobId is required to get a draft");
        }
        return await getValue([builderId, jobId]);
    };

    const removeDraft = async (): Promise<void> => {
        if (!jobId) {
            throw new Error("jobId is required to remove a draft");
        }
        await removeValue([builderId, jobId]);
    };

    return {
        setDraft,
        getDraft,
        removeDraft,
    };
}
